import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, PackageOpen, Check } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { zl } from "@/lib/shop";
import { shopApi, useShopAuth } from "@/lib/shopAuth";
import { addSizes } from "@/lib/b2bCart";
import RedfoxLayout from "./RedfoxLayout";
import { Button } from "@/components/ui/button";

export default function MyStoreProduct() {
  const { pid } = useParams();
  const auth = useShopAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [store, setStore] = useState(null);
  const [err, setErr] = useState("");
  const [color, setColor] = useState("");
  const [mainPhoto, setMainPhoto] = useState("");
  const [qty, setQty] = useState({}); // size -> qty

  useEffect(() => {
    if (!auth?.ready || !auth.customer) return;
    shopApi.get(`/b2b/my/products/${pid}`).then((r) => {
      setP(r.data);
      setColor((r.data.colors || [])[0] || "");
      setMainPhoto((r.data.photos || [])[0] || "");
      shopApi.get("/b2b/my/stores").then((s) => setStore((s.data || []).find((x) => x.id === r.data.store_id) || null)).catch(() => {});
    }).catch((e) => setErr(e?.response?.status === 403 ? "Brak dostępu do tego produktu." : "Produkt niedostępny."));
  }, [pid, auth?.ready, auth?.customer]);

  const disabled = useMemo(() => new Set(p?.disabled_variants || []), [p]);
  const sizeDisabled = (s) => disabled.has(`${color}|${s}`);
  const total = Object.values(qty).reduce((n, q) => n + (parseInt(q) || 0), 0);

  if (auth?.ready && !auth.customer) { navigate("/sklep/moj-sklep"); return null; }
  if (err) return <RedfoxLayout><div className="mx-auto max-w-md px-4 py-24 text-center text-neutral-400" data-testid="redfox-product-error">{err}</div></RedfoxLayout>;
  if (!p) return <RedfoxLayout><div className="py-24 text-center text-neutral-500">Ładowanie…</div></RedfoxLayout>;

  const add = () => {
    const clean = {};
    Object.entries(qty).forEach(([s, q]) => { const n = parseInt(q) || 0; if (n > 0 && !sizeDisabled(s)) clean[s] = n; });
    if (!Object.keys(clean).length) return toast.error("Wpisz ilość w co najmniej jednym rozmiarze");
    addSizes(p, color, clean);
    toast.success("Dodano do koszyka");
    setQty({});
  };

  return (
    <RedfoxLayout store={store}>
      <div className="mx-auto max-w-6xl px-4 py-6">
        <Link to="/sklep/moj-sklep" className="inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-white mb-6"><ArrowLeft className="h-4 w-4" />Powrót do kolekcji</Link>
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Galeria */}
          <div>
            <div className="aspect-[4/5] overflow-hidden rounded-2xl bg-neutral-900 p-3" data-testid="redfox-gallery-main">
              {mainPhoto ? <img src={`${API}/files/${mainPhoto}`} alt={p.name} className="h-full w-full object-contain" /> : <div className="grid h-full place-items-center text-neutral-600"><PackageOpen className="h-12 w-12" /></div>}
            </div>
            {(p.photos || []).length > 1 && (
              <div className="mt-3 flex gap-2 flex-wrap">
                {p.photos.map((ph) => (
                  <button key={ph} onClick={() => setMainPhoto(ph)} className={`h-16 w-16 overflow-hidden rounded-lg ring-2 transition ${mainPhoto === ph ? "ring-red-600" : "ring-transparent"}`}>
                    <img src={`${API}/files/${ph}`} alt="" className="h-full w-full object-contain bg-neutral-900" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info + wybór */}
          <div>
            <h1 className="text-3xl sm:text-4xl leading-tight uppercase italic" style={{ fontFamily: "'Oswald',sans-serif", fontWeight: 700 }}>{p.name}</h1>
            {p.subtitle && <p className="mt-1 text-neutral-400">{p.subtitle}</p>}
            <p className="mt-4 text-3xl" style={{ fontFamily: "'Oswald',sans-serif", fontWeight: 700 }}>{zl(p.price_grosz)}</p>
            {p.description && <p className="mt-4 text-sm text-neutral-300 whitespace-pre-line leading-relaxed">{p.description}</p>}

            {(p.colors || []).length > 0 && (
              <div className="mt-6">
                <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-2">Kolor</p>
                <div className="flex flex-wrap gap-2">
                  {p.colors.map((c) => (
                    <button key={c} data-testid={`redfox-color-${c}`} onClick={() => { setColor(c); setQty({}); }}
                      className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${color === c ? "bg-white text-neutral-950" : "bg-white/5 text-neutral-200 hover:bg-white/10"}`}>{c}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Wybór wielu rozmiarów */}
            <div className="mt-6">
              <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-2">Rozmiary i ilości</p>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="redfox-size-grid">
                {(p.sizes || []).map((s) => {
                  const off = sizeDisabled(s);
                  return (
                    <div key={s} className={`rounded-xl border p-2 text-center ${off ? "border-white/5 opacity-40" : "border-white/15"}`}>
                      <div className="text-sm font-bold">{s}</div>
                      {off ? <div className="mt-1 text-[10px] text-neutral-500">niedostępny</div> : (
                        <input data-testid={`redfox-qty-${s}`} type="number" min="0" inputMode="numeric" value={qty[s] ?? ""} placeholder="0"
                          onChange={(e) => { const v = e.target.value; setQty((q) => ({ ...q, [s]: v === "" ? "" : Math.max(0, parseInt(v) || 0) })); }}
                          className="mt-1 w-full bg-transparent text-center text-lg font-extrabold outline-none" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <Button data-testid="redfox-add-to-cart" onClick={add} disabled={total === 0}
              className="mt-6 w-full h-12 bg-red-600 hover:bg-red-500 text-white font-bold uppercase tracking-wider gap-2 disabled:opacity-40">
              <Check className="h-5 w-5" />Dodaj do koszyka{total > 0 ? ` (${total} szt.)` : ""}
            </Button>

            {p.size_table && (
              <div className="mt-8">
                <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-2">Tabela rozmiarów</p>
                <pre data-testid="redfox-size-table" className="whitespace-pre-wrap rounded-xl bg-neutral-900 p-4 text-xs text-neutral-300 font-mono">{p.size_table}</pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </RedfoxLayout>
  );
}
